/**
 * Lecture typee de `import.meta.env`.
 *
 * Tout ce qui est prefixe VITE_ est bundle donc PUBLIC : on n'y met jamais de
 * secret. Le CLIENT_SECRET Discord n'existe pas cote client, l'echange du code
 * OAuth se fait exclusivement dans la route serveur `/api/token`.
 *
 * Une seule variable subsiste : l'identifiant public de l'application Discord.
 * L'API vit sur la meme origine que la page, il n'y a donc aucune URL a
 * configurer.
 */

export interface ClientEnv {
  readonly discordClientId: string;
}

/** Cle .env -> message d'aide affiche si elle manque. */
const REQUIRED_KEYS = ['VITE_DISCORD_CLIENT_ID'] as const;

export type EnvKey = (typeof REQUIRED_KEYS)[number];

export class MissingEnvError extends Error {
  readonly missing: readonly EnvKey[];

  constructor(missing: readonly EnvKey[]) {
    super(
      `Configuration absente : ${missing.join(', ')}. ` +
        'Copie `.env.example` en `.env` a la racine du monorepo et renseigne ces valeurs, ' +
        'puis relance le serveur de developpement.',
    );
    this.name = 'MissingEnvError';
    this.missing = missing;
  }
}

/**
 * `import.meta.env` est type avec une signature d'index `any` par vite/client.
 * On le relit en `Record<string, unknown>` pour rester a zero `any`.
 */
function rawEnv(): Record<string, unknown> {
  return import.meta.env as unknown as Record<string, unknown>;
}

function readString(source: Record<string, unknown>, key: EnvKey): string | null {
  const value = source[key];
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

let cached: ClientEnv | null = null;

/**
 * Renvoie la configuration, ou leve une `MissingEnvError` listant precisement
 * les variables absentes (l'ecran de boot sait afficher ce message).
 */
export function getEnv(): ClientEnv {
  if (cached) return cached;

  const source = rawEnv();
  const values = new Map<EnvKey, string>();
  const missing: EnvKey[] = [];

  for (const key of REQUIRED_KEYS) {
    const value = readString(source, key);
    if (value === null) missing.push(key);
    else values.set(key, value);
  }

  if (missing.length > 0) throw new MissingEnvError(missing);

  // La cle est presente : le `??` ne sert qu'a satisfaire
  // noUncheckedIndexedAccess, il n'est jamais atteint.
  cached = {
    discordClientId: values.get('VITE_DISCORD_CLIENT_ID') ?? '',
  };
  return cached;
}

/** Vrai en `vite dev` : sert uniquement a moduler les logs, jamais la logique. */
export function isDev(): boolean {
  return import.meta.env.DEV === true;
}
